import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TicketTemplate {
  id: string;
  ticket_type_id: string;
  sales_item_id?: string;
  name: string;
  description?: string;
  is_default: boolean;
  sla_response_hours?: number;
  sla_resolution_hours?: number;
  auto_assign_to_role?: string;
  tags?: string[];
  created_at: string;
}

export interface TicketTaskTemplate {
  id: string;
  ticket_template_id: string;
  task_type_id: string;
  step_order: number;
  depends_on_step_order?: number;
  title_template: string;
  description_template?: string;
  default_assignee_role?: string;
  estimated_duration_hours?: number;
  is_required: boolean;
  is_blocking: boolean;
  trigger_type: 'immediate' | 'days_after_event' | 'on_event' | 'manual';
  trigger_days_after?: number;
  trigger_event_type_code?: string;
  created_at: string;
}

export const useTicketTemplates = () => {
  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['ticket-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_templates')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (template: any) => {
      const { data, error } = await supabase
        .from('ticket_templates')
        .insert([template])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      toast.error('Error creating template: ' + error.message);
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('ticket_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      toast.success('Template updated successfully');
    },
    onError: (error) => {
      toast.error('Error updating template: ' + error.message);
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ticket_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error) => {
      toast.error('Error deleting template: ' + error.message);
    },
  });

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};

export const useTicketTaskTemplates = (ticketTemplateId: string) => {
  const queryClient = useQueryClient();

  const { data: taskTemplates, isLoading } = useQuery({
    queryKey: ['ticket-task-templates', ticketTemplateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_task_templates')
        .select('*')
        .eq('ticket_template_id', ticketTemplateId)
        .order('step_order');

      if (error) throw error;
      return data;
    },
    enabled: !!ticketTemplateId,
  });

  const createTaskTemplate = useMutation({
    mutationFn: async (template: any) => {
      const { data, error } = await supabase
        .from('ticket_task_templates')
        .insert([template])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-task-templates'] });
      toast.success('Task template created successfully');
    },
    onError: (error) => {
      toast.error('Error creating task template: ' + error.message);
    },
  });

  const updateTaskTemplate = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('ticket_task_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-task-templates'] });
      toast.success('Task template updated successfully');
    },
    onError: (error) => {
      toast.error('Error updating task template: ' + error.message);
    },
  });

  const deleteTaskTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ticket_task_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-task-templates'] });
      toast.success('Task template deleted successfully');
    },
    onError: (error) => {
      toast.error('Error deleting task template: ' + error.message);
    },
  });

  return {
    taskTemplates,
    isLoading,
    createTaskTemplate,
    updateTaskTemplate,
    deleteTaskTemplate,
  };
};
